import Vue from "vue";
import cloneDeep from "lodash/cloneDeep";
import tableStoreService from "@/components/base_components/tables/services/table-store-service";
import tableService from "@/components/base_components/tables/services/table-service";
import store from '@/store';
import TABLE_DATA_KEYS from '@/components/base_components/tables/enum/table-data-keys';

const DEFAULT_SUB_TABLE_IDENTIFIER = "default";

const getCurrentTableIdentifier = ($route) => {
    return $route.query['tableCacheId'];
};

const getSetOfReferencedTableCacheIds = (routingObjects) => {
    const tableCacheIds = new Set();
    for (const routingObj of routingObjects) {
        const fromTableCacheId = routingObj?.from?.query?.['tableCacheId'];
        const toTableCacheId = routingObj?.to?.query?.['tableCacheId'];

        if (fromTableCacheId) { tableCacheIds.add(fromTableCacheId) }
        if (toTableCacheId) { tableCacheIds.add(toTableCacheId) }
    }
    return tableCacheIds;
}

const getAllUnusedTableCacheIds = (currentTables, referencedTableCacheIds) => {
    const unusedTables = Object.keys(currentTables)
        .filter((_tableCacheId) => referencedTableCacheIds.has(_tableCacheId) === false);

    return [...unusedTables]; // cast from SET to ARRAY
}

const copyWithoutVueWrappings = (o) => {
    if (o === null || o === undefined) return undefined;

    var output, v, key;
    output = Array.isArray(o) ? [] : {};
    for (key in o) {
        v = o[key];
        output[key] = (typeof v === "object") ? copyWithoutVueWrappings(v) : v;
    }
    return output;
}

export default {
    namespaced: true,
    state: {
        tables: {}
    },
    mutations: {
        storeNewTable(state, { identifier, tableObject }) {
            Vue.set(state.tables, identifier, copyWithoutVueWrappings(tableObject));
        },
        storeNewSubTable(state, { identifier, subTableIdentifier, subTableObject }) {
            Vue.set(state.tables[identifier].subTables, subTableIdentifier, copyWithoutVueWrappings(subTableObject));
        },
        setSearchExpression(state, { identifier, value }) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.SEARCH_EXPRESSION, value);
        },
        setFilterCardState(state, { identifier, value }) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTER_CARD_STATE, value);
        },
        setFilterValue(state, { identifier, key, value }) {
            Vue.set(state.tables[identifier].filters, key, value);
        },
        deleteFilter(state, { identifier, key }) {
            const clonedFilters = cloneDeep(state.tables[identifier].filters);

            if (clonedFilters[key] == undefined) { return }

            delete clonedFilters[key];
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTERS, clonedFilters);
        },
        setFilterOrderValue(state, { identifier, value }) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTER_ORDER, value);
        },
        setNotVisibleFilters(state, { identifier, value }) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS, value);
        },
        setHeaderOrderValue(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.HEADER_ORDER, value);
        },
        setNotVisibleHeaders(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS, value);
        },
        setSort(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, sortBy, sortDesc }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY, sortBy);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY_DESC, sortDesc);
        },
        setBulkOptions(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, bulkIds, excludeIdsSelectionMode, numberOfAffectedEntries }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.BULK_IDS, bulkIds);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.EXCLUDE_IDS_SELECTION_MODE, excludeIdsSelectionMode);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.NUMBER_OF_AFFECTED_ENTRIES, numberOfAffectedEntries);
        },
        setSortByDesc(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY_DESC, value);
        },
        setSortBy(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY, value);
        },
        setHeaders(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.HEADERS, value);
        },
        setHeaderOrder(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.HEADER_ORDER, value);
        },
        setPresetName(state, { identifier, value }) {
            Vue.set(state.tables[identifier].preset, 'name', value);
        },
        setPresetColumnSave(state, { identifier, value }) {
            Vue.set(state.tables[identifier].preset, 'saveColumns', value);
        },
        setPresetIsDefault(state, { identifier, value }) {
            Vue.set(state.tables[identifier].preset, 'isDefault', value);
        },
        setTruncateData(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, value }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.TRUNCATE_DATA, value);
        },
        setTruncateOptions(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, key, value }) {
            if (value === undefined) {
                return Vue.delete(state.tables[identifier].subTables[subTableIdentifier][TABLE_DATA_KEYS.TRUNCATE_OPTIONS], key);
            }

            if (state.tables[identifier].subTables[subTableIdentifier][TABLE_DATA_KEYS.TRUNCATE_OPTIONS] == undefined) {
                Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.TRUNCATE_OPTIONS, {});
            }
            Vue.set(state.tables[identifier].subTables[subTableIdentifier][TABLE_DATA_KEYS.TRUNCATE_OPTIONS], key, value);
        },
        setPresetObj(state, { identifier, presetObj }) {
            Vue.set(state.tables[identifier].preset, 'id', presetObj.id ?? null);
            Vue.set(state.tables[identifier].preset, 'name', presetObj.name ?? '');
            Vue.set(state.tables[identifier].preset, 'query', presetObj.query ?? '');
            Vue.set(state.tables[identifier].preset, 'isDefault', presetObj.isDefault ?? false);
            Vue.set(state.tables[identifier].preset, 'saveColumns', presetObj.saveColumns ?? false);
            Vue.set(state.tables[identifier].preset, 'entityVersion', presetObj.entityVersion ?? 0);
        },
        setFilterValues(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, filterObj }) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTERS, filterObj.filters);
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTER_ORDER, filterObj.filterOrder);
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS, filterObj.notVisibleFilters);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.TRUNCATE_DATA, filterObj.truncateData);

            if (filterObj.truncateOptions) {
                Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.TRUNCATE_OPTIONS, filterObj.truncateOptions);
            }
        },
        setColumnValues(state, { identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER, columnObj }) {
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY, columnObj.sortBy);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.SORT_BY_DESC, columnObj.sortByDesc);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.HEADERS, columnObj.headers);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.HEADER_ORDER, columnObj.headerOrder);
            Vue.set(state.tables[identifier].subTables[subTableIdentifier], TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS, columnObj.notVisibleHeaders);
        },
        clearFilters(state, identifier) {
            Vue.set(state.tables[identifier], TABLE_DATA_KEYS.FILTERS, {});
        },
        deleteTableReferences(state, tableReferences) {
            const clonedReferences = cloneDeep(state.tables);

            for (const identifier of tableReferences) {
                if (clonedReferences[identifier] !== undefined) {
                    delete clonedReferences[identifier];
                }
            }
            Vue.set(state, 'tables', clonedReferences);
        },
        resetStore(state) {
            Vue.set(state, 'tables', {});
        }
    },
    actions: {
        storeNewTable({ commit }, { table, identifier }) {
            const tableObject = tableStoreService.generateTableObject(table);

            commit('storeNewTable', { identifier, tableObject });
            return identifier;
        },
        storeNewSubTable({ commit, state }, { subTable, identifier, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            if (state.tables[identifier] === undefined) {
                commit('storeNewTable', {
                    identifier,
                    tableObject: tableStoreService.generateTableObject({})
                });
            }

            const subTableObject = tableStoreService.generateSubTableObject(subTable);
            commit('storeNewSubTable', {
                identifier,
                subTableIdentifier,
                subTableObject: subTableObject
            });
        },
        setFilterCardState({ commit }, { state, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setFilterCardState', { identifier, value: state });
        },
        setFilterValue({ commit }, { key, value, $route }) {
            const identifier = getCurrentTableIdentifier($route);

            if (tableService.isEmptyFilterValue(value)) {
                commit('deleteFilter', { identifier, key, value });
            } else {
                commit('setFilterValue', { identifier, key, value });
            }
        },
        setFilterOrder({ commit }, { order, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setFilterOrderValue', { identifier, value: order });
        },
        setNotVisibleFilters({ commit }, { notVisibleFilters, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setNotVisibleFilters', { identifier, value: notVisibleFilters });
        },
        setHeaderOrder({ commit }, { order, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setHeaderOrderValue', { identifier, subTableIdentifier, value: order });
        },
        setNotVisibleHeaders({ commit }, { notVisibleHeaders, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setNotVisibleHeaders', { identifier, subTableIdentifier, value: notVisibleHeaders });
        },
        setSort({ commit }, { sortBy, sortDesc, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setSort', { identifier, subTableIdentifier, sortBy, sortDesc });
        },
        setBulkOptions({ commit }, { bulkIds, excludeIdsSelectionMode, numberOfAffectedEntries, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setBulkOptions', { identifier, subTableIdentifier, bulkIds, excludeIdsSelectionMode, numberOfAffectedEntries });
        },
        setPresetName({ commit }, { name, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setPresetName', { identifier, value: name });
        },
        setPresetColumnSave({ commit }, { saveColumns, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setPresetColumnSave', { identifier, value: saveColumns });
        },
        setPresetIsDefault({ commit }, { presetIsDefault, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setPresetIsDefault', { identifier, value: presetIsDefault });
        },
        setPresetObj({ commit }, { presetObj, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setPresetObj', { identifier, presetObj });
        },
        setSearchExpression({ commit }, { search, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setSearchExpression', { identifier, value: search });
        },
        setTruncateData({ commit }, { truncateData, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setTruncateData', { identifier, subTableIdentifier, value: truncateData });
        },
        setTruncateOptions({ commit }, { key, value, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setTruncateOptions', { identifier, key, value, subTableIdentifier });
        },
        deleteFilter({ commit }, { searchKey, $route }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('deleteFilter', { identifier, key: searchKey });
        },
        setExistingPreset({ commit }, { preset, $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);

            if (preset === undefined || preset === null) {
                const emptyPreset = {};
                return commit('setPresetObj', { identifier, presetObj: emptyPreset });
            }

            const { columnObj, filterObj, presetObj } = tableService.getColumnsAndFiltersOf(preset);

            if (columnObj != undefined) {
                commit('setColumnValues', { identifier, columnObj, subTableIdentifier });
            }

            commit('setFilterValues', { identifier, subTableIdentifier, filterObj });
            commit('setPresetObj', { identifier, presetObj });

        },
        clearFilters({ commit }, $route) {
            const identifier = getCurrentTableIdentifier($route);
            commit('clearFilters', identifier);
            commit('setNotVisibleFilters', { identifier, value: [] });
        },
        resetTable({ commit }, { $route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER }) {
            const identifier = getCurrentTableIdentifier($route);
            commit('setFilterValues', {
                identifier,
                subTableIdentifier,
                filterObj: {
                    [TABLE_DATA_KEYS.FILTERS]: {},
                    [TABLE_DATA_KEYS.FILTER_ORDER]: [],
                    [TABLE_DATA_KEYS.NOT_VISIBLE_FILTERS]: [],
                    [TABLE_DATA_KEYS.TRUNCATE_DATA]: true,
                    [TABLE_DATA_KEYS.TRUNCATE_OPTIONS]: {}
                }
            });

            commit('setColumnValues', {
                identifier,
                columnObj: {
                    [TABLE_DATA_KEYS.SORT_BY]: [],
                    [TABLE_DATA_KEYS.SORT_BY_DESC]: [],
                    [TABLE_DATA_KEYS.HEADERS]: [],
                    [TABLE_DATA_KEYS.HEADER_ORDER]: [],
                    [TABLE_DATA_KEYS.NOT_VISIBLE_HEADERS]: [],
                }
            });
        },
        deleteAllTablesWithoutReference({ state, commit }) {
            let currentRoutingObjects = store.getters['routingHistory/getCurrentRoutingHistory'];
            const routingHistoryOfWindows = store.getters['activeWindows/getRoutingHistory'].flat();
            const routingObjects = [...currentRoutingObjects, ...routingHistoryOfWindows]

            const referencedTableCacheIds = getSetOfReferencedTableCacheIds(routingObjects);
            commit('deleteTableReferences', getAllUnusedTableCacheIds(state.tables, referencedTableCacheIds));
        },
        resetStore({ commit }) {
            commit('resetStore');
        }
    },
    getters: {
        getTable: (state) => ($route, subTableIdentifier = DEFAULT_SUB_TABLE_IDENTIFIER) => {
            const table = state.tables[getCurrentTableIdentifier($route)];
            if (table === undefined) {
                return undefined;
            }
            return tableStoreService.flattenTable(table, subTableIdentifier);
        },
    }
}

